import { Component, OnInit, ViewChild, AfterViewInit, HostListener } from '@angular/core';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';
import { Observable, Observer, Subject, Subscription } from 'rxjs';
import { ConfirmService } from '../confirm.service';

@Component({
  selector: 'app-confirm',
  templateUrl: './confirm.component.html',
  styleUrls: ['./confirm.component.less']
})
export class ConfirmComponent implements OnInit, AfterViewInit {

  @ViewChild('modal')
  modal!: DialogComponent;

  actionValue: boolean = false;
  cancelButton: boolean = true;

  // Responsivo
  dialogWidth: string = '640px';
  dialogHeight: string = '180px';

  // Limites (ajuste se quiser)
  private readonly minHeightPx = 160;
  private readonly maxHeightVh = 90; // 90% da altura da tela

  actionSubject: Subject<boolean> = new Subject<boolean>();

  constructor(private confirmService: ConfirmService) { }

  ngOnInit() {
    this.applyResponsiveSizing();
  }

  ngAfterViewInit(): void {
    this.confirmService.initService(this);
    // garante sizing correto caso o componente carregue já em tela pequena
    setTimeout(() => this.applyResponsiveSizing(), 0);
  }

  @HostListener('window:resize')
  onResize() {
    this.applyResponsiveSizing();
    // se estiver aberto, reajusta a altura também
    if (this.modal?.visible) {
      this.adjustDialogHeightToContent();
    }
  }

  show(title: string, content: string): Observable<boolean> {
    return new Observable<boolean>((observer: Observer<boolean>) => {

      const func = () => {
        this.modal.header = title;

        // Melhor: setar um wrapper pra medir sempre o conteúdo certo
        this.modal.content = `<div class="confirm-content">${content}</div>`;

        this.applyResponsiveSizing();
        this.modal.show();

        // Após renderizar, mede e ajusta a altura
        setTimeout(() => this.adjustDialogHeightToContent(), 0);
      };

      const subscription: Subscription = this.actionSubject.subscribe((v: boolean) => {
        subscription.unsubscribe();
        observer.next(v);
        observer.complete();
      });

      if (this.modal) {
        func();
      } else {
        setTimeout(() => func(), 500);
      }

      return { unsubscribe: function() {} };
    });
  }

  okClick() {
    this.actionValue = true;
    this.modal.hide();
  }

  close() {
    this.actionValue = false;
    this.modal.hide();
  }

  onClose() {
    this.actionSubject.next(this.actionValue);
    this.actionValue = false;
  }

  setHeight(height: string) {
    this.dialogHeight = height;
    if (this.modal) this.modal.height = height;
  }

  showCancelButton(show: boolean) {
    this.cancelButton = show;

    // Se mudar footer, recalcula altura
    if (this.modal?.visible) {
      setTimeout(() => this.adjustDialogHeightToContent(), 0);
    }
  }

  // ---------------------------
  // Responsividade
  // ---------------------------
  private applyResponsiveSizing() {
    const w = window.innerWidth;

    // Mobile
    if (w <= 576) {
      this.dialogWidth = '92vw';
    }
    // Tablet
    else if (w <= 992) {
      this.dialogWidth = '82vw';
    }
    // Desktop
    else {
      this.dialogWidth = '640px';
    }

    // Mantém a altura "base" mínima (a altura final vai ser recalculada)
    this.dialogHeight = `${this.minHeightPx}px`;

    if (this.modal) {
      this.modal.width = this.dialogWidth;
      this.modal.height = this.dialogHeight;
    }
  }

  // ---------------------------
  // Auto-height (medição real)
  // ---------------------------
  private adjustDialogHeightToContent() {
    if (!this.modal?.element) return;

    const dlgEl = this.modal.element as HTMLElement;

    const headerEl = dlgEl.querySelector('.e-dlg-header-content') as HTMLElement | null;
    const contentEl = dlgEl.querySelector('.e-dlg-content') as HTMLElement | null;
    const footerEl = dlgEl.querySelector('.e-footer-content') as HTMLElement | null;

    if (!contentEl) return;

    // Mede alturas reais
    const headerH = headerEl?.offsetHeight ?? 0;
    const footerH = footerEl?.offsetHeight ?? 0;

    // scrollHeight do conteúdo (pega todo o texto)
    const contentH = contentEl.scrollHeight;

    // Alguns pixels de respiro
    const padding = 16;

    const desired = headerH + contentH + footerH + padding;

    // Limita ao máximo da viewport
    const maxPx = Math.floor(window.innerHeight * (this.maxHeightVh / 100));
    const finalPx = Math.max(this.minHeightPx, Math.min(desired, maxPx));

    this.dialogHeight = `${finalPx}px`;
    this.modal.height = this.dialogHeight;

    // Se estourar o max, deixa o scroll dentro do conteúdo (via CSS também)
    // Aqui só garantimos um comportamento coerente:
    contentEl.style.overflowY = (desired > maxPx) ? 'auto' : 'visible';
  }
}
